// People & Compliance Plan: HR and SLT planning of performance, pay,
// governance, culture and certification work, moved in from the Claude
// artifact El and Harry B kept until October 2026.
//
// The server decides what can be seen: restricted items arrive only for
// someone holding people-plan:slt, and data.can_restricted says so. Groups
// (SLT, HR, line managers, middle managers) come from the staff directory.
// The planning rules live in utils/peoplePlan.js, shared with the API.
import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../hooks/useApi';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import FeatureTour from '../shared/FeatureTour';
import { heatCellClass } from '../shared/LoadBar';
import { formatDateLong } from '../../utils/format';
import {
  CATEGORIES, TYPES, STATUSES, PRIORITIES, GROUP_KEYS, GROUP_LABELS,
  rollingWindow, monthRange, isChangeWindow, daysInMonth, dayNumber, period,
  computeLoad, netCapacity, itemPeople, planPeople, normaliseItem, DEFAULT_CAPACITY,
} from '../../utils/peoplePlan';
import { downloadPlanExport } from './peoplePlanExport';
import { PEOPLE_PLAN_TOUR_ID, peoplePlanTourSteps } from './peoplePlanTour';

const TABS = [
  { id: 'timeline', label: 'Timeline' }, { id: 'workload', label: 'Workload' }, { id: 'list', label: 'All items' },
  { id: 'standing', label: 'Standing commitments' }, { id: 'people', label: 'People' },
];
// Mid-tone category colours read on both the light and the dark page.
const CAT_COLOUR = {
  'Performance & development': '#4F6FA0', 'Pay & reward': '#A1723D', 'Organisation & governance': '#5E8A6E',
  'Culture & engagement': '#8E5E80', 'Certifications': '#4F7F8F',
};
const HATCH = 'repeating-linear-gradient(135deg, transparent 0 6px, rgba(127,127,127,.14) 6px 12px)';
const NOW_TINT = 'rgba(161,114,61,.12)';
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const monthLong = (ym) => `${MONTH_NAMES[+ym.slice(5, 7) - 1]} ${ym.slice(0, 4)}`;
const monthShort = (ym) => MONTH_NAMES[+ym.slice(5, 7) - 1].slice(0, 3);
const round1 = (n) => Math.round(n * 10) / 10;
const lines = (s) => String(s || '').split(/\n+/).map(x => x.trim()).filter(Boolean);
const hasEstimate = (t) => (t.effort || []).some(e => e.est);
const todayIso = () => new Date().toISOString().slice(0, 10);
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

const btn = 'px-3 py-1.5 text-[9px] font-semibold border border-gray-200 rounded bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50';
const btnPrimary = 'px-3 py-1.5 text-[9px] font-bold bg-navy text-white rounded hover:bg-navy/80 disabled:opacity-50';
const input = 'border border-gray-200 rounded px-2 py-1 text-[10px] bg-white text-navy';
const pill = 'inline-block px-1.5 py-0.5 rounded text-[8px] font-bold whitespace-nowrap';

const BLANK = {
  title: '', category: CATEGORIES[0], type: 'task', owner: '', involved: [], signoff: [], start: '', deadline: '',
  status: 'Not started', priority: 'Medium', visibility: 'company', contractors: false, effort: [], milestones: [],
  notes: '', process: '', questions: '', assumptions: '', depends: [],
};

export default function PeoplePlanPage({ tab: initialTab }) {
  const [data, setData] = useState({ items: [], standing: [], people: [], headcount: { employees: 0, contractors: 0 }, can_restricted: false });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [tab, setTab] = useState(TABS.some(t => t.id === initialTab) ? initialTab : 'timeline');
  const [win, setWin] = useState(() => rollingWindow(todayIso()));
  const [person, setPerson] = useState('');
  const [companyView, setCompanyView] = useState(false);
  const [estOnly, setEstOnly] = useState(false);
  const [open, setOpen] = useState(null); // { id, editing } or { id: null, editing: true } for a new item

  const load = useCallback(() => api.get('people-plan')
    .then(d => { setData(d); setLoadError(''); })
    .catch(e => setLoadError(e?.data?.error || e?.message || 'Could not load the plan'))
    .finally(() => setLoading(false)), []);
  useEffect(() => { load(); }, [load]);
  const tour = useFeatureTour(PEOPLE_PLAN_TOUR_ID, { enabled: !loading && !loadError });

  const months = useMemo(() => monthRange(win.from, win.to), [win]);
  const people = data.people;
  const nameOf = useCallback((w) => GROUP_LABELS[w] || people.find(p => p.id === w)?.name || (w ? 'Unknown' : 'TBC'), [people]);
  const shown = useMemo(() => data.items.filter(t => !companyView || t.visibility === 'company'), [data.items, companyView]);
  const rows = useMemo(() => planPeople(shown, people), [shown, people]);
  const filtered = useMemo(() => shown.filter(t => {
    if (!person) return true;
    const ids = itemPeople(t, people);
    return ids.has(person);
  }).sort((a, b) => a.deadline.localeCompare(b.deadline) || a.title.localeCompare(b.title)), [shown, person, people]);
  const estimates = data.items.filter(hasEstimate).length;

  const switchTab = (id) => { setTab(id); window.history.replaceState(null, '', `#people-plan/${id}`); };
  const openItem = (id) => setOpen({ id, editing: false });

  if (loading) return <div className="text-center py-8 text-[10px] text-gray-400">Loading…</div>;
  if (loadError) return <div className="text-center py-8 text-[10px] text-red-600">{loadError}</div>;

  const current = open && (open.id ? data.items.find(t => t.id === open.id) : { ...BLANK, owner: '' });

  return (
    <div className="fade-in">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3" data-tour="page-header">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-base font-extrabold text-navy">People &amp; Compliance Plan</h1>
            <button onClick={tour.start} className="w-[23px] h-[23px] rounded-full border border-gray-200 text-gray-500 hover:text-navy hover:border-navy text-[11px] font-bold" aria-label="Replay the guided tour of this page" title="Replay the guided tour of this page">?</button>
          </div>
          <div className="text-[9px] text-gray-400 mt-0.5">
            {monthLong(win.from)} to {monthLong(win.to)}. Change windows are March and September.
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {estimates > 0 && (
            <button onClick={() => { setEstOnly(true); switchTab('list'); }} className="px-2.5 py-1 text-[9px] font-semibold rounded-full border border-gold text-gold bg-gold/10">
              {estimates} {estimates === 1 ? 'item has' : 'items have'} estimates to check
            </button>
          )}
          <span className="flex items-center gap-1 text-[9px] text-gray-500" data-tour="window">
            <input type="month" aria-label="First month" className={input} value={win.from} max={win.to} onChange={e => e.target.value && setWin(w => ({ ...w, from: e.target.value }))} />
            to
            <input type="month" aria-label="Last month" className={input} value={win.to} min={win.from} onChange={e => e.target.value && setWin(w => ({ ...w, to: e.target.value }))} />
            <button className={btn} onClick={() => setWin(rollingWindow(todayIso()))}>Today</button>
          </span>
          <select aria-label="Show items for a person" data-tour="person-filter" className={input} value={person} onChange={e => setPerson(e.target.value)}>
            <option value="">Everyone</option>
            {rows.map(p => <option key={p.id} value={p.id}>{p.name}{p.left ? ' (left)' : ''}</option>)}
            <option value="__all">All employees</option>
          </select>
          {data.can_restricted && (
            <select aria-label="View" data-tour="view" className={input} value={companyView ? 'company' : 'full'} onChange={e => setCompanyView(e.target.value === 'company')}>
              <option value="full">Full view</option>
              <option value="company">Preview company view</option>
            </select>
          )}
          <button className={btn} onClick={() => downloadPlanExport({ items: shown, people: rows, standing: data.standing, months })}>Export to Excel</button>
          <button className={btnPrimary} data-tour="add-item" onClick={() => setOpen({ id: null, editing: true })}>Add item</button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 mb-3" role="tablist" data-tour="tabs">
        {TABS.map(t => (
          <button key={t.id} role="tab" aria-selected={tab === t.id} onClick={() => switchTab(t.id)}
            className={`px-3 py-1 text-[10px] font-semibold rounded ${tab === t.id ? 'bg-navy text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'timeline' && <Timeline items={filtered} months={months} onOpen={openItem} person={person} load={person ? computeLoad(shown, people, months).L[person] || {} : null} cap={person && person !== '__all' ? netCapacity(people.find(p => p.id === person) || {}) : 1} personName={person === '__all' ? 'Days per employee' : `${nameOf(person)}'s days`} />}
      {tab === 'workload' && <Workload items={shown} people={people} rows={rows} months={months} headcount={data.headcount} onOpen={openItem} />}
      {tab === 'list' && <ItemList items={estOnly ? filtered.filter(hasEstimate) : filtered} estOnly={estOnly} setEstOnly={setEstOnly} nameOf={nameOf} onOpen={openItem} />}
      {tab === 'standing' && <Standing standing={data.standing} people={people} nameOf={nameOf} onSaved={load} />}
      {tab === 'people' && <PeopleTab rows={rows} headcount={data.headcount} onSaved={load} />}

      {current && (
        <ItemDrawer key={open.id || 'new'} item={current} isNew={!open.id} editing={open.editing} setEditing={(e) => setOpen(o => ({ ...o, editing: e }))}
          data={data} nameOf={nameOf} onOpen={openItem} onClose={() => setOpen(null)}
          onSaved={(id) => { load(); setOpen(id ? { id, editing: false } : null); }} />
      )}
      {tour.open && <FeatureTour steps={peoplePlanTourSteps} context={{ canRestricted: data.can_restricted }} onFinish={tour.finish} onSkip={tour.skip} />}
    </div>
  );
}

/* ── Timeline ───────────────────────────────────────────────────────────── */

function Timeline({ items, months, onOpen, person, load, cap, personName }) {
  const first = dayNumber(`${months[0]}-01`);
  const last = dayNumber(`${months[months.length - 1]}-${daysInMonth(months[months.length - 1])}`) + 1;
  const pos = (d) => ((dayNumber(d) - first) / (last - first)) * 100;
  const nowMonth = todayIso().slice(0, 7);
  const cols = { gridTemplateColumns: `minmax(220px, 300px) repeat(${months.length}, minmax(48px, 1fr))` };
  const background = (
    <div className="absolute inset-y-0 left-0 right-0 grid pointer-events-none" style={{ gridTemplateColumns: `repeat(${months.length}, 1fr)` }}>
      {months.map(m => <span key={m} className="border-r border-dashed border-gray-200" style={{ background: m === nowMonth ? NOW_TINT : isChangeWindow(m) ? HATCH : undefined }} />)}
    </div>
  );
  const inWindow = items.filter(t => { const p = period(t); return p && p[1] >= `${months[0]}-01` && p[0] <= `${months[months.length - 1]}-31`; });
  return (
    <div>
      <p className="text-[9px] text-gray-400 mb-2">Bars run from the first day of the start month to the date it must be done by. Circles are milestones and the diamond is the deadline. Hatched months are change windows, and the shaded month is this one.</p>
      <div className="bg-white border border-gray-200 rounded overflow-x-auto">
        <div className="grid min-w-[1000px] text-[10px]" style={cols}>
          <div className="sticky left-0 z-10 bg-gray-50 border-b border-r border-gray-200 px-2 py-1.5 text-[8px] uppercase text-gray-400 font-bold">Item</div>
          {months.map((m, i) => (
            <div key={m} className="bg-gray-50 border-b border-gray-200 text-center py-1" style={{ background: m === nowMonth ? NOW_TINT : undefined }}>
              <div className="text-[8px] font-bold text-gold">{i === 0 || m.endsWith('-01') ? m.slice(0, 4) : ' '}</div>
              <div className="text-[9px] font-semibold text-navy">{monthShort(m)}</div>
            </div>
          ))}
          {load && (
            <>
              <div className="sticky left-0 z-10 bg-white border-b border-r border-gray-200 px-2 py-1.5 font-bold text-navy">{personName}</div>
              <div className="border-b border-gray-200 grid" style={{ gridColumn: `2 / span ${months.length}`, gridTemplateColumns: `repeat(${months.length}, 1fr)` }}>
                {months.map(m => <div key={m} className={`text-center py-1.5 border-r border-dashed border-gray-200 text-navy ${person === '__all' ? '' : heatCellClass(load[m] || 0, cap)}`}>{load[m] ? round1(load[m]) : ''}</div>)}
              </div>
            </>
          )}
          {CATEGORIES.map(c => {
            const ts = inWindow.filter(t => t.category === c);
            if (!ts.length) return null;
            return [
              <div key={c} className="sticky left-0 bg-gray-50 border-b border-gray-200 px-2 py-1.5 font-bold text-navy flex items-center gap-2" style={{ gridColumn: '1 / -1' }}>
                <span className="w-2.5 h-2.5 rounded-sm" style={{ background: CAT_COLOUR[c] }} />{c}
              </div>,
              ...ts.flatMap(t => {
                const [s, e] = period(t);
                const l = Math.max(0, pos(s)), r = Math.min(100, pos(e) + 100 / (last - first));
                return [
                  <div key={`${t.id}-n`} className="sticky left-0 z-10 bg-white border-b border-r border-gray-200 px-2 py-1.5 flex items-center gap-1 flex-wrap">
                    <button className="text-left font-semibold text-navy hover:text-gold" onClick={() => onOpen(t.id)}>{t.title}</button>
                    {hasEstimate(t) && <span className={`${pill} bg-amber-50 text-amber-700`}>est.</span>}
                    {t.visibility === 'slt' && <span className={`${pill} bg-blue-50 text-blue-700`}>SLT only</span>}
                  </div>,
                  <div key={`${t.id}-t`} className="relative border-b border-gray-200 min-h-[30px]" style={{ gridColumn: `2 / span ${months.length}` }}>
                    {background}
                    {t.type !== 'milestone' && r > l && (
                      <button aria-label={t.title} onClick={() => onOpen(t.id)} className="absolute top-[8px] h-[14px] rounded-sm opacity-90 hover:opacity-100"
                        style={{ left: `${l}%`, width: `${r - l}%`, background: CAT_COLOUR[t.category] }} />
                    )}
                    {pos(t.deadline) >= 0 && pos(t.deadline) <= 100 && (
                      <button title={`Must be done by ${formatDateLong(t.deadline)}`} aria-label={`Must be done by ${formatDateLong(t.deadline)}`} onClick={() => onOpen(t.id)}
                        className={`absolute top-[9px] w-3 h-3 rotate-45 border-2 border-white -ml-1.5 ${t.type === 'milestone' ? 'bg-navy' : 'bg-gold'}`}
                        style={{ left: `min(${r}%, calc(100% - 8px))` }} />
                    )}
                    {(t.milestones || []).filter(x => x.date && pos(x.date) >= 0 && pos(x.date) <= 100).map((x, i) => (
                      <button key={i} title={`${x.label}: ${formatDateLong(x.date)}${x.done ? ' (done)' : ''}`} aria-label={x.label} onClick={() => onOpen(t.id)}
                        className={`absolute top-[8px] w-3.5 h-3.5 rounded-full border-[3px] border-navy -ml-[7px] ${x.done ? 'bg-navy' : 'bg-white'}`} style={{ left: `${pos(x.date)}%` }} />
                    ))}
                  </div>,
                ];
              }),
            ];
          })}
        </div>
        {!inWindow.length && <div className="text-center py-6 text-[10px] text-gray-400">Nothing in these months. Move the window or add an item.</div>}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[9px] text-gray-500">
        {CATEGORIES.map(c => <span key={c} className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: CAT_COLOUR[c] }} />{c}</span>)}
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full border-2 border-navy" />Milestone, filled when done</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rotate-45 bg-gold" />Must be done by</span>
        <span className="flex items-center gap-1"><span className="w-3 h-2.5 border border-gray-200" style={{ background: HATCH }} />Change window</span>
      </div>
    </div>
  );
}

/* ── Workload ───────────────────────────────────────────────────────────── */

function Workload({ items, people, rows, months, headcount, onOpen }) {
  const [sel, setSel] = useState(null);
  const { L, C } = useMemo(() => computeLoad(items, people, months), [items, people, months]);
  const cell = (id, m, cls, children) => {
    const on = sel && sel[0] === id && sel[1] === m;
    return (
      <td key={m} tabIndex={0} onClick={() => setSel(on ? null : [id, m])} onKeyDown={e => e.key === 'Enter' && setSel(on ? null : [id, m])}
        className={`text-center px-1 py-1.5 border-l border-gray-100 cursor-pointer tabular-nums text-navy ${cls} ${on ? 'outline outline-2 -outline-offset-2 outline-navy' : ''}`}>{children}</td>
    );
  };
  const breakdown = sel ? C[`${sel[0]}|${sel[1]}`] || [] : [];
  const selName = sel && (sel[0] === '__all' ? 'All employees' : people.find(p => p.id === sel[0])?.name);
  return (
    <div>
      <p className="text-[9px] text-gray-400 mb-2">Days each person spends on planned items each month, spread by calendar day across each item&apos;s period. Colours compare the days with capacity after standing commitments, set on the People tab. Select a cell to see what makes it up.</p>
      <div className="bg-white border border-gray-200 rounded overflow-x-auto">
        <table className="w-full text-[10px]">
          <thead className="bg-gray-50 text-[8px] uppercase text-gray-400">
            <tr>
              <th className="text-left px-2 py-1.5 sticky left-0 bg-gray-50 min-w-[180px]">Person</th>
              {months.map(m => <th key={m} className="px-1 py-1.5 text-center">{monthShort(m)}<div className="font-normal">{m.slice(2, 4)}</div></th>)}
              <th className="px-2 py-1.5 text-center">Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(p => {
              const cap = netCapacity(p);
              const r = L[p.id] || {};
              const total = months.reduce((t, m) => t + (r[m] || 0), 0);
              return (
                <tr key={p.id} className="border-t border-gray-100">
                  <td className="px-2 py-1.5 sticky left-0 bg-white">
                    <div className="font-semibold text-navy">{p.name}{p.left && <span className={`${pill} ml-1 bg-gray-100 text-gray-600`}>Left</span>}</div>
                    <div className="text-[8px] text-gray-400">{[p.role, `${round1(cap)} of ${p.capacity ?? DEFAULT_CAPACITY} days a month free for planned work`].filter(Boolean).join(', ')}</div>
                  </td>
                  {months.map(m => cell(p.id, m, r[m] ? heatCellClass(r[m], cap) : '', r[m] ? round1(r[m]) : ''))}
                  <td className="px-2 py-1.5 text-center font-bold text-navy">{total ? round1(total) : ''}</td>
                </tr>
              );
            })}
            <tr className="border-t-2 border-gray-200">
              <td className="px-2 py-1.5 sticky left-0 bg-white">
                <div className="font-semibold text-navy">All employees</div>
                <div className="text-[8px] text-gray-400">Days each, and the total across {headcount.employees} active employees</div>
              </td>
              {months.map(m => {
                const v = L.__all?.[m] || 0;
                return cell('__all', m, v ? 'bg-gray-50' : '', v ? <>{round1(v)}<div className="text-[8px] text-gray-400">{round1(v * headcount.employees)} total</div></> : '');
              })}
              <td className="px-2 py-1.5 text-center font-bold text-navy">{(() => { const t = months.reduce((s, m) => s + (L.__all?.[m] || 0), 0); return t ? <>{round1(t)}<div className="text-[8px] text-gray-400 font-normal">{round1(t * headcount.employees)} total</div></> : ''; })()}</td>
            </tr>
          </tbody>
        </table>
      </div>
      {sel && (
        <div className="bg-white border border-gray-200 rounded mt-3 p-3">
          <div className="text-[11px] font-bold text-navy mb-2">{selName}, {monthLong(sel[1])}: {round1(breakdown.reduce((t, x) => t + x.days, 0))} days</div>
          {breakdown.length ? (
            <table className="w-full text-[10px]">
              <thead className="bg-gray-50 text-[8px] uppercase text-gray-400"><tr><th className="text-left px-2 py-1">Item</th><th className="text-left px-2 py-1">Through</th><th className="text-left px-2 py-1">Days this month</th></tr></thead>
              <tbody>
                {breakdown.map((x, i) => (
                  <tr key={i} className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => onOpen(x.item.id)}>
                    <td className="px-2 py-1 text-navy">{x.item.title}</td>
                    <td className="px-2 py-1 text-gray-600">{GROUP_LABELS[x.via] || 'Named on the item'}</td>
                    <td className="px-2 py-1 text-navy">{round1(x.days)} {x.est && <span className={`${pill} bg-amber-50 text-amber-700`}>est.</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <div className="text-[10px] text-gray-400">Nothing planned.</div>}
        </div>
      )}
    </div>
  );
}

/* ── All items ──────────────────────────────────────────────────────────── */

function ItemList({ items, estOnly, setEstOnly, nameOf, onOpen }) {
  const statusPill = (s) => (s === 'Done' ? 'bg-green-50 text-green-700' : s === 'In progress' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600');
  return (
    <div>
      <div className="flex items-center gap-3 mb-2">
        <label className="flex items-center gap-1 text-[9px] text-gray-600"><input type="checkbox" checked={estOnly} onChange={e => setEstOnly(e.target.checked)} /> Only items with estimates to check</label>
        <span className="text-[9px] text-gray-400">{items.length} {items.length === 1 ? 'item' : 'items'}, including any outside the months shown</span>
      </div>
      <div className="bg-white border border-gray-200 rounded overflow-x-auto">
        <table className="w-full text-[10px]">
          <thead className="bg-gray-50 text-[8px] uppercase text-gray-400">
            <tr>{['Item', 'Category', 'Owner', 'Work window', 'Key dates', 'Status', 'Effort'].map(h => <th key={h} className="text-left px-2 py-1.5">{h}</th>)}</tr>
          </thead>
          <tbody>
            {items.map(t => {
              const [s] = period(t);
              return (
                <tr key={t.id} className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => onOpen(t.id)}>
                  <td className="px-2 py-1.5 font-semibold text-navy">{t.title} {t.visibility === 'slt' && <span className={`${pill} bg-blue-50 text-blue-700`}>SLT only</span>}</td>
                  <td className="px-2 py-1.5 text-gray-600"><span className="inline-block w-2 h-2 rounded-sm mr-1" style={{ background: CAT_COLOUR[t.category] }} />{t.category}</td>
                  <td className="px-2 py-1.5 text-gray-600">{nameOf(t.owner)}</td>
                  <td className="px-2 py-1.5 text-gray-600 whitespace-nowrap">{s.slice(0, 7) === t.deadline.slice(0, 7) ? monthLong(s.slice(0, 7)) : `${monthLong(s.slice(0, 7))} to ${monthLong(t.deadline.slice(0, 7))}`}</td>
                  <td className="px-2 py-1.5 text-gray-600">
                    {(t.milestones || []).filter(x => x.date).map((x, i) => <div key={i} className="text-[9px] text-gray-400">{x.label}: {formatDateLong(x.date)}{x.done ? ' (done)' : ''}</div>)}
                    <div className="font-semibold text-navy whitespace-nowrap">{formatDateLong(t.deadline)}</div>
                  </td>
                  <td className="px-2 py-1.5"><span className={`${pill} ${statusPill(t.status)}`}>{t.status}</span></td>
                  <td className="px-2 py-1.5 text-gray-600">{(t.effort || []).map(e => `${nameOf(e.who)} ${round1(e.days)}`).join(', ') || 'None'} {hasEstimate(t) && <span className={`${pill} bg-amber-50 text-amber-700`}>est.</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!items.length && <div className="text-center py-6 text-[10px] text-gray-400">No items match.</div>}
      </div>
    </div>
  );
}

/* ── Standing commitments ───────────────────────────────────────────────── */

function Standing({ standing, people, nameOf, onSaved }) {
  const [list, setList] = useState(standing);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');
  const [armed, setArmed] = useState(false);
  useEffect(() => setList(standing), [standing]);
  const active = people.filter(p => !p.left).sort((a, b) => a.name.localeCompare(b.name));
  const save = async (next) => {
    try { await api.put('people-plan/standing', { list: next }); setEditing(null); setError(''); setArmed(false); onSaved(); }
    catch (e) { setError(e?.data?.error || e?.message || 'Save failed'); }
  };
  const field = (k, v) => setList(l => l.map((s, i) => (i === editing ? { ...s, [k]: v } : s)));
  return (
    <div>
      <p className="text-[9px] text-gray-400 mb-2">Regular meetings and ongoing work that sit outside the timeline. Each person&apos;s standing days on the People tab take them off the capacity the workload uses.</p>
      <div className="grid gap-2 md:grid-cols-2">
        {list.map((s, i) => (i === editing ? (
          <div key={s.id || i} className="bg-white border border-navy rounded p-3 text-[10px] grid gap-2">
            <div className="grid grid-cols-2 gap-2">
              <label className="grid gap-0.5 text-[9px] text-gray-500">Name<input className={input} value={s.title} onChange={e => field('title', e.target.value)} /></label>
              <label className="grid gap-0.5 text-[9px] text-gray-500">Cadence<input className={input} value={s.cadence} onChange={e => field('cadence', e.target.value)} /></label>
              <label className="grid gap-0.5 text-[9px] text-gray-500">Owner
                <select className={input} value={s.owner} onChange={e => field('owner', e.target.value)}>
                  <option value="">TBC</option>{active.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </label>
              <label className="grid gap-0.5 text-[9px] text-gray-500">Status
                <select className={input} value={s.status} onChange={e => field('status', e.target.value)}>{['Running', 'To agree', 'Paused'].map(o => <option key={o}>{o}</option>)}</select>
              </label>
            </div>
            <label className="grid gap-0.5 text-[9px] text-gray-500">Attendees<input className={input} value={s.attendees} onChange={e => field('attendees', e.target.value)} /></label>
            <label className="grid gap-0.5 text-[9px] text-gray-500">Notes<textarea className={`${input} min-h-[60px]`} value={s.notes} onChange={e => field('notes', e.target.value)} /></label>
            <div className="flex items-center gap-2">
              <button className={btnPrimary} onClick={() => save(list)}>Save changes</button>
              <button className={btn} onClick={() => { setList(standing); setEditing(null); setError(''); }}>Cancel</button>
              <button className={`${btn} ml-auto ${armed ? 'text-red-700 border-red-300' : ''}`} onClick={() => (armed ? save(list.filter((_, j) => j !== i)) : setArmed(true))}>{armed ? 'Click again to delete' : 'Delete'}</button>
            </div>
            {error && <div className="text-[9px] text-red-600">{error}</div>}
          </div>
        ) : (
          <div key={s.id || i} className="bg-white border border-gray-200 rounded p-3 text-[10px]">
            <div className="flex items-start justify-between gap-2 mb-1">
              <div className="font-bold text-navy text-[11px]">{s.title} <span className={`${pill} ${s.status === 'Running' ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{s.status}</span></div>
              <button className={btn} onClick={() => { setEditing(i); setArmed(false); }} disabled={editing != null}>Edit</button>
            </div>
            <dl className="grid grid-cols-[90px_1fr] gap-x-2 gap-y-0.5 text-gray-600">
              <dt className="text-gray-400">Cadence</dt><dd>{s.cadence || 'TBC'}</dd>
              <dt className="text-gray-400">Owner</dt><dd>{nameOf(s.owner)}</dd>
              <dt className="text-gray-400">Attendees</dt><dd>{s.attendees || 'TBC'}</dd>
              {s.notes && <><dt className="text-gray-400">Notes</dt><dd>{s.notes}</dd></>}
            </dl>
          </div>
        )))}
      </div>
      {editing == null && <button className={`${btn} mt-2`} onClick={() => { setList(l => [...l, { title: '', cadence: '', owner: '', attendees: '', status: 'To agree', notes: '' }]); setEditing(list.length); }}>Add standing commitment</button>}
    </div>
  );
}

/* ── People ─────────────────────────────────────────────────────────────── */

function PeopleTab({ rows, headcount, onSaved }) {
  return (
    <div>
      <p className="text-[9px] text-gray-400 mb-2">
        Groups come from the staff directory and change there: line managers are people with direct reports, SLT is the Senior Management Team, HR is the HR team, and middle managers have the Middle Manager role. Capacity is the days a month each person can give to planned items. Standing days are what regular meetings take, and come off capacity before the workload colours are worked out.
      </p>
      <div className="bg-white border border-gray-200 rounded overflow-x-auto">
        <table className="w-full text-[10px]">
          <thead className="bg-gray-50 text-[8px] uppercase text-gray-400">
            <tr>{['Name', 'Role', 'Groups', 'Capacity (days a month)', 'Standing (days a month)', 'Free for planned work', ''].map(h => <th key={h} className="text-left px-2 py-1.5">{h}</th>)}</tr>
          </thead>
          <tbody>{rows.map(p => <CapacityRow key={p.id} p={p} onSaved={onSaved} />)}</tbody>
        </table>
      </div>
      <div className="bg-white border border-gray-200 rounded p-3 mt-3 max-w-md text-[10px]">
        <div className="font-bold text-navy mb-1">Headcount from the staff directory</div>
        <div className="text-gray-600">{plural(headcount.employees, 'active employee', 'active employees')} and {plural(headcount.contractors, 'active contractor', 'active contractors')}. The employee figure turns days each into a total on the Workload tab.</div>
      </div>
    </div>
  );
}

function CapacityRow({ p, onSaved }) {
  const [cap, setCap] = useState(p.capacity ?? '');
  const [standing, setStanding] = useState(p.standing_days ?? '');
  const [msg, setMsg] = useState('');
  const dirty = String(cap) !== String(p.capacity ?? '') || String(standing) !== String(p.standing_days ?? '');
  const save = async () => {
    try {
      await api.put(`people-plan/capacity/${p.id}`, { capacity: cap === '' ? DEFAULT_CAPACITY : Number(cap), standing_days: standing === '' ? 0 : Number(standing) });
      setMsg('Saved'); onSaved();
    } catch (e) { setMsg(e?.data?.error || e?.message || 'Save failed'); }
  };
  return (
    <tr className="border-t border-gray-100">
      <td className="px-2 py-1.5 font-semibold text-navy">{p.name}{p.left && <span className={`${pill} ml-1 bg-gray-100 text-gray-600`}>Left</span>}</td>
      <td className="px-2 py-1.5 text-gray-600">{p.role}</td>
      <td className="px-2 py-1.5">
        {p.groups.length ? p.groups.map(g => <span key={g} className={`${pill} mr-1 bg-gray-100 text-gray-600`}>{GROUP_LABELS[g]}</span>) : <span className="text-gray-400">None</span>}
        {p.type === 'contractor' && <span className={`${pill} bg-amber-50 text-amber-700`}>Contractor</span>}
      </td>
      <td className="px-2 py-1.5"><input type="number" min="0" max="31" step="0.5" aria-label={`${p.name} capacity`} placeholder={String(DEFAULT_CAPACITY)} className={`${input} w-20`} value={cap} onChange={e => { setCap(e.target.value); setMsg(''); }} /></td>
      <td className="px-2 py-1.5"><input type="number" min="0" max="31" step="0.25" aria-label={`${p.name} standing days`} placeholder="0" className={`${input} w-20`} value={standing} onChange={e => { setStanding(e.target.value); setMsg(''); }} /></td>
      <td className="px-2 py-1.5 text-navy">{round1(netCapacity({ capacity: cap === '' ? null : cap, standing_days: standing }))}</td>
      <td className="px-2 py-1.5 whitespace-nowrap">
        {dirty && <button className={btnPrimary} onClick={save}>Save</button>}
        {msg && <span className={`ml-2 text-[9px] ${msg === 'Saved' ? 'text-green-700' : 'text-red-600'}`}>{msg}</span>}
      </td>
    </tr>
  );
}

/* ── Item drawer ────────────────────────────────────────────────────────── */

function ItemDrawer({ item, isNew, editing, setEditing, data, nameOf, onOpen, onClose, onSaved }) {
  const [draft, setDraft] = useState(() => JSON.parse(JSON.stringify(item)));
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const send = async (body) => {
    const { error: bad } = normaliseItem(body);
    if (bad) { setError(bad); return; }
    setSaving(true);
    try {
      const res = isNew ? await api.post('people-plan/items', body) : await api.put(`people-plan/items/${item.id}`, body);
      onSaved(res.item.id);
    } catch (e) { setError(e?.data?.error || e?.message || 'Save failed'); setSaving(false); }
  };
  const remove = async () => {
    if (!armed) { setArmed(true); return; }
    try { await api.delete(`people-plan/items/${item.id}`); onSaved(null); }
    catch (e) { setError(e?.data?.error || e?.message || 'Delete failed'); }
  };

  const deps = (item.depends || []).map(d => data.items.find(x => x.id === d)).filter(Boolean);
  const feeds = data.items.filter(x => (x.depends || []).includes(item.id));
  const span = item.deadline ? period(item) : null;

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div role="dialog" aria-modal="true" aria-label={item.title || 'New item'} className="fixed top-0 right-0 bottom-0 w-full max-w-xl z-50 bg-white shadow-2xl flex flex-col border-l-4 border-gold">
        <div className="px-4 py-3 border-b border-gray-200 flex items-start justify-between gap-3">
          <div>
            <div className="text-[13px] font-extrabold text-navy">{isNew ? 'New item' : item.title}</div>
            {!isNew && <div className="text-[9px] text-gray-500 mt-0.5"><span className="inline-block w-2 h-2 rounded-sm mr-1" style={{ background: CAT_COLOUR[item.category] }} />{item.category}, done by {formatDateLong(item.deadline)}</div>}
          </div>
          <button className={btn} onClick={onClose}>Close</button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 text-[10px]">
          {editing ? <ItemForm draft={draft} setDraft={setDraft} data={data} itemId={item.id} /> : (
            <div className="grid gap-4">
              <Section title="Details">
                <dl className="grid grid-cols-[120px_1fr] gap-x-3 gap-y-0.5 text-gray-700">
                  <dt className="text-gray-400">Owner</dt><dd>{nameOf(item.owner)}</dd>
                  <dt className="text-gray-400">Involved</dt><dd>{item.involved.map(nameOf).join(', ') || 'None listed'}</dd>
                  <dt className="text-gray-400">Sign-off</dt><dd>{item.signoff.map(nameOf).join(', ') || 'None'}</dd>
                  <dt className="text-gray-400">Work window</dt><dd>{span && (span[0].slice(0, 7) === item.deadline.slice(0, 7) ? monthLong(item.deadline.slice(0, 7)) : `${monthLong(span[0].slice(0, 7))} to ${monthLong(item.deadline.slice(0, 7))}`)}</dd>
                  <dt className="text-gray-400">Status</dt><dd>{item.status}</dd>
                  <dt className="text-gray-400">Priority</dt><dd>{item.priority}</dd>
                  <dt className="text-gray-400">Type</dt><dd className="capitalize">{item.type}</dd>
                  <dt className="text-gray-400">Visible to</dt><dd>{item.visibility === 'slt' ? 'SLT only: the SLT and anyone given restricted items' : 'Everyone who can open the plan'}</dd>
                  <dt className="text-gray-400">Affects contractors</dt><dd>{item.contractors ? 'Yes' : 'No'}</dd>
                </dl>
              </Section>
              <Section title="Key dates">
                <ul className="list-disc pl-4 text-gray-700">
                  {(item.milestones || []).filter(x => x.date).map((x, i) => <li key={i}>{x.label}: <b>{formatDateLong(x.date)}</b>{x.done && <span className={`${pill} ml-1 bg-green-50 text-green-700`}>Done</span>}</li>)}
                  <li>Must be done by: <b>{formatDateLong(item.deadline)}</b></li>
                </ul>
              </Section>
              <Section title="Effort">
                {item.effort.length ? (
                  <table className="w-full">
                    <tbody>
                      {item.effort.map((e, i) => (
                        <tr key={i} className="border-t border-gray-100">
                          <td className="py-1 text-navy">{nameOf(e.who)}{GROUP_KEYS.includes(e.who) && <span className="text-[8px] text-gray-400 ml-1">{e.who === 'all_employees' ? 'days each' : `days each, ${plural(data.people.filter(p => !p.left && p.groups.includes(e.who)).length, 'person', 'people')}`}</span>}</td>
                          <td className="py-1 text-navy">{plural(round1(e.days), 'day', 'days')}</td>
                          <td className="py-1">{e.est ? <span className={`${pill} bg-amber-50 text-amber-700`}>Estimate, please check</span> : <span className={`${pill} bg-green-50 text-green-700`}>Confirmed</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : <div className="text-gray-400">No effort recorded.</div>}
                {hasEstimate(item) && <button className={`${btn} mt-2`} disabled={saving} onClick={() => send({ ...item, effort: item.effort.map(e => ({ ...e, est: false })) })}>Mark all effort as confirmed</button>}
              </Section>
              {item.assumptions && <Section title="Assumptions to check"><div className="bg-amber-50 border-l-4 border-gold rounded px-3 py-2 whitespace-pre-wrap text-gray-700">{item.assumptions}</div></Section>}
              <Section title="What has been said"><div className="bg-gray-50 border border-gray-200 rounded px-3 py-2 whitespace-pre-wrap text-gray-700">{item.notes || 'Nothing yet.'}</div></Section>
              <Section title="Proposed process (draft)">
                {lines(item.process).length ? <ol className="list-decimal pl-4 text-gray-700">{lines(item.process).map((l, i) => <li key={i}>{l.replace(/^\d+[.)]\s*/, '')}</li>)}</ol> : <div className="text-gray-400">Not drafted yet.</div>}
              </Section>
              <Section title="Open questions">
                {lines(item.questions).length ? <ul className="list-disc pl-4 text-gray-700">{lines(item.questions).map((l, i) => <li key={i}>{l}</li>)}</ul> : <div className="text-gray-400">None.</div>}
              </Section>
              {(deps.length > 0 || feeds.length > 0) && (
                <Section title="Linked items">
                  {deps.length > 0 && <div>Depends on: {deps.map((d, i) => <span key={d.id}>{i > 0 && ', '}<button className="text-gold underline" onClick={() => onOpen(d.id)}>{d.title}</button></span>)}</div>}
                  {feeds.length > 0 && <div>Feeds into: {feeds.map((d, i) => <span key={d.id}>{i > 0 && ', '}<button className="text-gold underline" onClick={() => onOpen(d.id)}>{d.title}</button></span>)}</div>}
                </Section>
              )}
              <div className="text-[9px] text-gray-400 border-t border-gray-100 pt-2">
                Last changed {item.updated_by_name ? `by ${item.updated_by_name} ` : ''}on {formatDateLong(`${String(item.updated_at || '').slice(0, 10)}T12:00:00`)}. Every change is kept in the audit log.
              </div>
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t border-gray-200 flex items-center gap-2">
          {error && <span className="mr-auto text-[9px] text-red-600">{error}</span>}
          {editing ? (
            <>
              <button className={`${btn} ${error ? '' : 'ml-auto'}`} onClick={() => (isNew ? onClose() : (setDraft(JSON.parse(JSON.stringify(item))), setEditing(false), setError('')))}>Cancel</button>
              <button className={btnPrimary} disabled={saving} onClick={() => send(draft)}>{saving ? 'Saving…' : isNew ? 'Add item' : 'Save changes'}</button>
            </>
          ) : (
            <>
              <button className={`${btn} ${error ? '' : 'mr-auto'} ${armed ? 'text-red-700 border-red-300' : ''}`} onClick={remove}>{armed ? 'Click again to delete' : 'Delete item'}</button>
              <button className={btnPrimary} onClick={() => setEditing(true)}>Edit</button>
            </>
          )}
        </div>
      </div>
    </>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h4 className="inline-block text-[9px] font-bold uppercase tracking-wide text-navy border-b-2 border-gold pb-0.5 mb-1.5">{title}</h4>
      {children}
    </section>
  );
}

function WhoSelect({ value, onChange, options, label, blank = 'TBC' }) {
  return (
    <select aria-label={label} className={input} value={value} onChange={e => onChange(e.target.value)}>
      <option value="">{blank}</option>
      <optgroup label="Groups">{GROUP_KEYS.map(g => <option key={g} value={g}>{GROUP_LABELS[g]}</option>)}</optgroup>
      <optgroup label="People">{options.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</optgroup>
    </select>
  );
}

function WhoList({ value, onChange, options, label, nameOf }) {
  return (
    <div className="flex flex-wrap items-center gap-1">
      {value.map(w => (
        <span key={w} className={`${pill} bg-gray-100 text-gray-700 flex items-center gap-1`}>{nameOf(w)}
          <button aria-label={`Remove ${nameOf(w)}`} onClick={() => onChange(value.filter(x => x !== w))}>×</button>
        </span>
      ))}
      <WhoSelect value="" label={label} blank="Add…" options={options} onChange={w => w && !value.includes(w) && onChange([...value, w])} />
    </div>
  );
}

function ItemForm({ draft, setDraft, data, itemId }) {
  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));
  const active = data.people.filter(p => !p.left).sort((a, b) => a.name.localeCompare(b.name));
  // Keep a leaver who is already on the item selectable, so editing never drops them.
  const named = new Set([draft.owner, ...draft.involved, ...draft.signoff, ...draft.effort.map(e => e.who)]);
  const options = [...active, ...data.people.filter(p => p.left && named.has(p.id))];
  const nameOf = (w) => GROUP_LABELS[w] || data.people.find(p => p.id === w)?.name || 'Unknown';
  const label = 'grid gap-0.5 text-[9px] text-gray-500';
  const row = (k, i, patch) => set(k, draft[k].map((x, j) => (j === i ? { ...x, ...patch } : x)));
  return (
    <div className="grid gap-2">
      <label className={label}>Title<input className={input} value={draft.title} onChange={e => set('title', e.target.value)} /></label>
      <div className="grid grid-cols-2 gap-2">
        <label className={label}>Category<select className={input} value={draft.category} onChange={e => set('category', e.target.value)}>{CATEGORIES.map(c => <option key={c}>{c}</option>)}</select></label>
        <label className={label}>Type<select className={input} value={draft.type} onChange={e => set('type', e.target.value)}>{TYPES.map(c => <option key={c}>{c}</option>)}</select></label>
        <label className={label}>Owner<WhoSelect label="Owner" value={draft.owner} options={options} onChange={v => set('owner', v)} /></label>
        <label className={label}>Status<select className={input} value={draft.status} onChange={e => set('status', e.target.value)}>{STATUSES.map(c => <option key={c}>{c}</option>)}</select></label>
        <label className={label}>Work starts (month)<input type="month" className={input} value={draft.start} onChange={e => set('start', e.target.value)} /></label>
        <label className={label}>Must be done by<input type="date" className={input} value={draft.deadline} onChange={e => set('deadline', e.target.value)} /></label>
        <label className={label}>Priority<select className={input} value={draft.priority} onChange={e => set('priority', e.target.value)}>{PRIORITIES.map(c => <option key={c}>{c}</option>)}</select></label>
        {data.can_restricted && (
          <label className={label}>Visible to
            <select className={input} value={draft.visibility} onChange={e => set('visibility', e.target.value)}>
              <option value="company">Everyone who can open the plan</option><option value="slt">SLT only</option>
            </select>
          </label>
        )}
      </div>
      <div className={label}>Involved<WhoList label="Add someone involved" value={draft.involved} options={options} nameOf={nameOf} onChange={v => set('involved', v)} /></div>
      <div className={label}>Sign-off<WhoList label="Add someone to sign off" value={draft.signoff} options={options} nameOf={nameOf} onChange={v => set('signoff', v)} /></div>
      <label className="flex items-center gap-1 text-[9px] text-gray-600"><input type="checkbox" checked={draft.contractors} onChange={e => set('contractors', e.target.checked)} /> Affects contractors</label>

      <div className={label}>
        Effort (days in total for a person; for a group, days for each member)
        {draft.effort.map((e, i) => (
          <div key={i} className="flex items-center gap-1">
            <WhoSelect label="Who" value={e.who} options={options} onChange={v => row('effort', i, { who: v })} />
            <input type="number" min="0" step="0.25" aria-label="Days" className={`${input} w-20`} value={e.days} onChange={ev => row('effort', i, { days: ev.target.value, est: false })} />
            <label className="flex items-center gap-1 text-gray-600"><input type="checkbox" checked={e.est} onChange={ev => row('effort', i, { est: ev.target.checked })} /> Estimate</label>
            <button className={btn} onClick={() => set('effort', draft.effort.filter((_, j) => j !== i))}>Remove</button>
          </div>
        ))}
        <button className={`${btn} justify-self-start`} onClick={() => set('effort', [...draft.effort, { who: '', days: 1, est: true }])}>Add effort</button>
      </div>

      <div className={label}>
        Milestones before the deadline
        {draft.milestones.map((x, i) => (
          <div key={i} className="flex items-center gap-1">
            <input className={`${input} flex-1`} aria-label="Milestone" placeholder="For example: process signed off" value={x.label} onChange={e => row('milestones', i, { label: e.target.value })} />
            <input type="date" aria-label="Milestone date" className={input} value={x.date} onChange={e => row('milestones', i, { date: e.target.value })} />
            <label className="flex items-center gap-1 text-gray-600"><input type="checkbox" checked={x.done} onChange={e => row('milestones', i, { done: e.target.checked })} /> Done</label>
            <button className={btn} onClick={() => set('milestones', draft.milestones.filter((_, j) => j !== i))}>Remove</button>
          </div>
        ))}
        <button className={`${btn} justify-self-start`} onClick={() => set('milestones', [...draft.milestones, { label: '', date: '', done: false }])}>Add milestone</button>
      </div>

      <label className={label}>What has been said<textarea className={`${input} min-h-[70px]`} value={draft.notes} onChange={e => set('notes', e.target.value)} /></label>
      <label className={label}>Proposed process (draft, one step per line)<textarea className={`${input} min-h-[110px]`} value={draft.process} onChange={e => set('process', e.target.value)} /></label>
      <label className={label}>Open questions (one per line)<textarea className={`${input} min-h-[60px]`} value={draft.questions} onChange={e => set('questions', e.target.value)} /></label>
      <label className={label}>Assumptions to check<textarea className={`${input} min-h-[60px]`} value={draft.assumptions} onChange={e => set('assumptions', e.target.value)} /></label>
      <label className={label}>Depends on
        <select multiple size={5} className={input} value={draft.depends} onChange={e => set('depends', [...e.target.selectedOptions].map(o => o.value))}>
          {data.items.filter(x => x.id !== itemId).sort((a, b) => a.title.localeCompare(b.title)).map(x => <option key={x.id} value={x.id}>{x.title}</option>)}
        </select>
      </label>
    </div>
  );
}
